import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Box, Typography, CircularProgress, Alert, Checkbox, FormControlLabel, Snackbar } from "@mui/material";
import {
  fetchSessionOpenings,
  createSessionOpening,
  updateSessionOpening,
  deleteSessionOpening,
  postSessionOpenings,
  closeSessionOpenings,
} from "../../api/backend";
import { useConfigList } from "../../hooks/useConfigList";
import ConfirmDialog from "../../components/ConfirmDialog";
import { C, PeopleIcon, outlineBtn, primaryBtn } from "./staffing/staffingUi";
import { SlotCell, SignupSummary } from "./staffing/SlotCell";
import SessionMenu from "./staffing/SessionMenu";
import OpenToInstructorsDialog from "./staffing/OpenToInstructorsDialog";
import { getInstructorName } from "./staffing/people";
import {
  LEAD_ROLE, ASSISTANT_ROLE, isSessionPosted, currentSettings, startWizard, buildPostPayload,
  countsAfterAdd, upcomingSessions, formatSessionRange, sessionLabel, formatSessionDate, todayIso,
} from "./staffing/staffingModel";

export { getInstructorName };

const DEFAULT_OTHER_TEACHING_ROLES = [ASSISTANT_ROLE, "TA"];
const WIDE = "@media (min-width:1001px)";
const COMPACT = "@media (max-width:1000px)";
const GRID = "minmax(150px,1fr) minmax(0,1.2fr) minmax(0,1.2fr) minmax(0,1fr) 150px 210px";

const roleCaption = (role) => (role === LEAD_ROLE ? "Lead" : role === ASSISTANT_ROLE ? "Assistant" : role);
const isPastSession = (s) => {
  const d = s.sessionDate || s.date;
  return Boolean(d) && d.slice(0, 10) < todayIso();
};

export default function SessionStaffingPanel({ sessions = [], instructorMap, canEdit, courseInstructors = [], blocked = () => false }) {
  const [openingsBySession, setOpeningsBySession] = useState({});
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [onlyOpen, setOnlyOpen] = useState(false);
  const [assigning, setAssigning] = useState(null);
  const [removing, setRemoving] = useState(null);
  const [wizard, setWizard] = useState(null);
  const [toast, setToast] = useState(null);
  const otherRoles = useConfigList("TEACHING_ROLE", DEFAULT_OTHER_TEACHING_ROLES);
  const roles = useMemo(
    () => [{ value: LEAD_ROLE, label: "Lead" }, ...otherRoles.map((r) => ({ value: r, label: roleCaption(r) }))]
      .filter((r, i, arr) => arr.findIndex((x) => x.value === r.value) === i),
    [otherRoles],
  );
  const roleCodes = useMemo(() => roles.map((r) => r.value), [roles]);

  const loadSession = useCallback((sessionId) => {
    fetchSessionOpenings(sessionId).then((data) => {
      setOpeningsBySession((prev) => ({ ...prev, [sessionId]: data }));
    }).catch(() => {});
  }, []);

  const loadAll = useCallback(() => Promise.all(
    sessions.map((s) => fetchSessionOpenings(s.id).then((d) => [s.id, d]).catch(() => [s.id, []])),
  ).then((pairs) => Object.fromEntries(pairs)), [sessions]);

  useEffect(() => {
    let cancelled = false;
    if (sessions.length === 0) { setLoading(false); return undefined; }
    setLoading(true);
    loadAll().then((bySession) => {
      if (cancelled) return;
      setOpeningsBySession(bySession);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [sessions, loadAll]);

  const fail = (fallback) => (e) => setErr(e?.response?.data?.message || fallback);

  const sorted = useMemo(() => [...sessions].sort((a, b) =>
    (a.sessionDate || a.date || "").localeCompare(b.sessionDate || b.date || "")), [sessions]);
  const upcoming = useMemo(() => upcomingSessions(sessions), [sessions]);

  // Adding a slot to a session that is already open to instructors posts it with the same settings; otherwise it stays hidden.
  const handleAdd = (sessionId, teachingRole) => {
    const openings = openingsBySession[sessionId] || [];
    const settings = isSessionPosted(openings) ? currentSettings(openings) : null;
    const request = settings
      ? postSessionOpenings(sessionId, undefined, buildPostPayload({
        counts: countsAfterAdd(openings, roleCodes, teachingRole), mode: settings.mode, limit: settings.limit, levels: settings.levels,
      }))
      : createSessionOpening(sessionId, undefined, { teachingRole });
    return request.then(() => loadSession(sessionId)).catch(fail("Failed to add opening."));
  };

  const handleAssign = (sessionId, openingId, instructorId) => {
    setAssigning(null);
    return updateSessionOpening(sessionId, openingId, undefined, { assignedInstructorId: instructorId })
      .then(() => loadSession(sessionId)).catch(fail("Failed to assign instructor."));
  };

  // Returns the promise so the confirmation dialog can show the server's reason (e.g. a session must keep one opening).
  const handleRemove = ({ sessionId, openingId }) =>
    deleteSessionOpening(sessionId, openingId, undefined).then(() => loadSession(sessionId));

  const handleCloseSignups = (s) =>
    closeSessionOpenings(s.id, undefined)
      .then(() => { loadSession(s.id); setToast({ text: `Sign-ups closed for ${sessionLabel(s)}.` }); })
      .catch(fail("Could not close sign-ups."));

  const beginWizard = (s) => setWizard({
    session: s,
    initial: startWizard({ openings: openingsBySession[s.id] || [], roleCodes }),
  });

  // One request per session. Sessions that saved stay saved if another one fails; the error names how many did not.
  const submitWizard = async (state) => {
    const payload = buildPostPayload(state);
    const targets = state.scope === "all" ? upcoming : [wizard.session];
    const results = await Promise.allSettled(targets.map((s) => postSessionOpenings(s.id, undefined, payload)));
    setOpeningsBySession(await loadAll());
    const failed = results.filter((r) => r.status === "rejected");
    if (failed.length) {
      const reason = failed[0].reason?.response?.data?.message || failed[0].reason?.message || "Could not save.";
      throw new Error(failed.length === targets.length ? reason : `${failed.length} of ${targets.length} sessions could not be updated: ${reason}`);
    }
    setToast(state.edit
      ? { text: "Sign-up settings saved." }
      : { text: `${state.scope === "all" ? "All sessions are" : `${sessionLabel(wizard.session)} is`} open to instructors.`, link: true });
    setWizard(null);
  };

  const all = sorted.flatMap((s) => openingsBySession[s.id] || []);
  const filled = all.filter((o) => o.status !== "OPEN").length;
  const pct = all.length ? (filled / all.length) * 100 : 0;
  const rows = sorted.filter((s) => !onlyOpen || (openingsBySession[s.id] || []).some((o) => o.status === "OPEN"));

  const column = (s, openings, role) => {
    const items = openings.filter((o) => (role === "OTHER"
      ? o.teachingRole !== LEAD_ROLE && o.teachingRole !== ASSISTANT_ROLE
      : o.teachingRole === role));
    const caption = { display: "block", [WIDE]: { display: "none" }, font: "600 10.5px var(--font-ui)", letterSpacing: ".2px",
      textTransform: "uppercase", color: C.muted };
    const label = role === LEAD_ROLE ? "Lead instructor" : role === ASSISTANT_ROLE ? "Assistant instructor" : "TA";
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: "6px", alignItems: "flex-start", minWidth: 0,
        [COMPACT]: { gridColumn: "1 / -1" } }}>
        <Box component="span" sx={caption}>{label}</Box>
        {items.length === 0 ? <Box component="span" sx={{ color: C.lineStrong }}>—</Box> : (
          <>
            {items.map((o) => (
              <SlotCell key={o.id} opening={o} instructorMap={instructorMap} candidates={courseInstructors}
                blocked={blocked} canEdit={canEdit} canRemove={openings.length > 1}
                assigning={assigning === o.id}
                onStartAssign={setAssigning} onCancelAssign={() => setAssigning(null)}
                onAssign={(openingId, instructorId) => handleAssign(s.id, openingId, instructorId)}
                onUnassign={(openingId) => handleAssign(s.id, openingId, null)}
                onRemove={(op) => setRemoving({ sessionId: s.id, openingId: op.id, filled: Boolean(op.assignedInstructorId) })} />
            ))}
            <SignupSummary openings={items.filter((o) => o.status === "OPEN")} />
          </>
        )}
      </Box>
    );
  };

  const sessionRow = (s) => {
    const openings = openingsBySession[s.id] || [];
    const open = openings.filter((o) => o.status === "OPEN").length;
    const posted = isSessionPosted(openings);
    const pending = openings.reduce((n, o) => n + (o.status === "OPEN" && o.signupMode === "APPROVAL" ? o.pendingSignupCount || 0 : 0), 0);
    const label = sessionLabel(s);
    return (
      <Box key={s.id} data-testid={`session-row-${s.id}`}
        sx={{ display: "grid", gridTemplateColumns: GRID, gap: "12px 16px", alignItems: "center", p: "12px 20px",
          "& + &": { borderTop: `1px solid ${C.line}` }, "&:hover": { bgcolor: "#fcfcfd" },
          [COMPACT]: { gridTemplateColumns: "minmax(0,1fr) auto" } }}>
        <Box sx={{ [COMPACT]: { gridColumn: 1 } }}>
          <Box sx={{ fontWeight: 600, fontSize: 14 }}>{label}</Box>
          <Box sx={{ fontSize: 12.5, color: C.muted }}>{formatSessionDate(s)}</Box>
        </Box>
        {openings.length === 0
          ? <Box sx={{ gridColumn: { xs: "1 / -1", [WIDE]: "2 / 5" }, fontSize: 12.5, color: C.muted, fontStyle: "italic" }}>No openings</Box>
          : <>{column(s, openings, LEAD_ROLE)}{column(s, openings, ASSISTANT_ROLE)}{column(s, openings, "OTHER")}</>}
        <Box sx={{ display: "flex", flexDirection: "column", gap: "2px", fontSize: 13, [COMPACT]: { gridColumn: 1, gridRow: 2 } }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <Box component="i" sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: open ? C.warn : C.success }} />
            {open ? `${open} open` : "Staffed"}
          </Box>
          {open > 0 && posted && (
            <Box component="span" sx={{ display: "flex", alignItems: "center", gap: "5px", pl: "13px", fontSize: 11.5, fontWeight: 600, color: C.violet }}>
              Visible to instructors
            </Box>
          )}
          {pending > 0 && (
            <Box component={RouterLink} to="/coordinator/approvals" sx={{ pl: "13px", fontSize: 12, fontWeight: 600, color: C.warn }}>
              {pending} pending · Review
            </Box>
          )}
        </Box>
        <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: "6px", [COMPACT]: { gridColumn: 2, gridRow: 1 } }}>
          {canEdit && open > 0 && !isPastSession(s) && (
            <Box component="button" type="button" onClick={() => beginWizard(s)}
              aria-label={posted ? `Edit sign-ups for ${label}` : `Open ${label} to instructors`}
              sx={{ ...(posted ? outlineBtn : primaryBtn), height: 32, px: "12px" }}>
              {!posted && <PeopleIcon />}
              {posted ? "Edit sign-ups" : "Open to instructors"}
            </Box>
          )}
          {canEdit && (
            <SessionMenu label={label} roles={roles} posted={posted && open > 0}
              onAdd={(role) => handleAdd(s.id, role)} onClose={() => handleCloseSignups(s)} />
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Box component="section" sx={{ mt: "36px" }}>
      <Box sx={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: "12px", flexWrap: "wrap", mb: "12px" }}>
        <Box>
          <Typography component="h2" sx={{ m: 0, fontSize: 18, fontWeight: 700, color: C.ink }}>Session staffing</Typography>
          <Typography sx={{ mt: "2px", fontSize: 13, color: C.muted }}>
            Assign instructors directly, or open a session to instructors so they can request it.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: "12px", fontSize: 13 }}>
          <span><b>{filled} of {all.length}</b> openings filled</span>
          <Box sx={{ width: 140, height: 6, borderRadius: "3px", bgcolor: C.line, overflow: "hidden" }}>
            <Box sx={{ width: `${pct}%`, height: "100%", bgcolor: C.success }} />
          </Box>
        </Box>
      </Box>

      <FormControlLabel sx={{ mb: "8px", "& .MuiFormControlLabel-label": { fontSize: 13 } }}
        control={<Checkbox size="small" checked={onlyOpen} onChange={(e) => setOnlyOpen(e.target.checked)} />}
        label="Only show sessions with open slots" />

      {err && <Alert severity="error" onClose={() => setErr(null)} sx={{ mb: 1, borderRadius: "12px" }}>{err}</Alert>}

      <Box sx={{ bgcolor: C.surface, border: `1px solid ${C.line}`, borderRadius: "16px", boxShadow: "var(--shadow-card-sm)", overflow: "hidden" }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}><CircularProgress size={20} sx={{ color: C.primary }} /></Box>
        ) : sorted.length === 0 ? (
          <Box sx={{ p: "40px 20px", textAlign: "center", color: C.muted, fontSize: 13.5 }}>No sessions scheduled yet.</Box>
        ) : rows.length === 0 ? (
          <Box sx={{ p: "40px 20px", textAlign: "center", color: C.muted, fontSize: 13.5 }}>Every session is fully staffed.</Box>
        ) : (
          <>
            <Box sx={{ display: "none", [WIDE]: { display: "grid" }, gridTemplateColumns: GRID, gap: "16px", p: "10px 20px",
              borderBottom: `1px solid ${C.line}`, fontSize: 11, fontWeight: 600, letterSpacing: ".2px", textTransform: "uppercase", color: C.muted }}>
              <span>Session</span><span>Lead instructor</span><span>Assistant instructor</span><span>TA</span><span>Status</span><span />
            </Box>
            {rows.map(sessionRow)}
          </>
        )}
      </Box>

      {wizard && (
        <OpenToInstructorsDialog key={wizard.session.id} session={wizard.session} sessionCount={upcoming.length}
          rangeText={formatSessionRange(upcoming)} roles={roleCodes} initial={wizard.initial}
          onClose={() => setWizard(null)} onSubmit={submitWizard} />
      )}

      <Snackbar open={Boolean(toast)} autoHideDuration={5000} onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        message={toast && (
          <span>
            {toast.text}{" "}
            {toast.link && <RouterLink to="/instructor/scheduling" style={{ color: "var(--color-accent-lime)" }}>See what they see</RouterLink>}
          </span>
        )} />

      <ConfirmDialog
        open={Boolean(removing)}
        title="Remove this opening?"
        message={removing?.filled
          ? "This slot is filled. Removing it also takes that instructor off this session."
          : "This open teaching slot will be removed from the session."}
        confirmLabel="Remove opening"
        busyLabel="Removing…"
        onConfirm={() => handleRemove(removing)}
        onClose={() => setRemoving(null)}
      />
    </Box>
  );
}
